import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAuthContext, UserModel } from '@picsart/growth-rc/AuthContext';
import { CheckoutViewType } from '@picsart/monetization/types/models/checkoutModel';

import useAnalytics from 'services/Analytics';
import { getIsPromoCodeActive } from 'services/Subscription';

import useLandingPropsStore from 'stores/landingPropsStore';
import useMonetizationDataStore from 'stores/monetizationDataStore';

import fetchData from 'api/fetch';
import { apiUrl, isCustomWelcomePL, PremiumPackages } from 'constants/main';
import useScreenName from 'hooks/useScreenName';

const defaultErrorMessage =
  "Oops! It looks like you're already subscribed. If you have any questions or need assistance, please <a href='https://www.revolut.com/contact-us/'>contact our support team.</a>";

const useCustomFlows = (promoRequired?: boolean) => {
  const { fireEvent } = useAnalytics();
  const screenName = useScreenName();
  const redeemApplied = useRef<boolean>(false);
  const [errMessage, setErrMessage] = useState<string>('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const { user, onUserChange, tokenData } = useAuthContext();
  const [searchParams] = useSearchParams();
  const monetizationPopupTrigger = useMonetizationDataStore(x => x.monetizationPopupTrigger);
  const premiumPopupPackage = useLandingPropsStore(x => x.premiumPopupPackage);
  const category = useLandingPropsStore(x => x.category);
  const isDiscord = premiumPopupPackage === PremiumPackages.discordNitro;
  const isPartnership = category === 'partnership';
  const isSamsung = screenName === 'samsung';

  const customRedeemFlow = useMemo(
    () => premiumPopupPackage === 'custom_API_flow',
    [premiumPopupPackage],
  );
  const specialView = isSamsung || customRedeemFlow ? 'welcome' : 'plans';
  const modalView: CheckoutViewType = promoRequired ? 'promo' : specialView;

  const isCustomWelcomePopup = isCustomWelcomePL(screenName) || customRedeemFlow;

  const sendAnalyticsForCustomFlows = useCallback(
    (status: string, statusCode?: number, message?: string) => {
      fireEvent?.('subscription_promo_redeem_response', {
        status,
        status_code: !statusCode && status === 'fail' ? '500' : statusCode.toString(),
        description: message || null,
        promo_id: screenName,
      });
    },
    [fireEvent, screenName],
  );

  const checkPromoIsActive = useCallback(async () => {
    if (!redeemApplied.current) {
      monetizationPopupTrigger(true);
      const queryVal = {
        code: decodeURIComponent(searchParams.get('code')),
      };

      try {
        redeemApplied.current = true;
        const isPromoCodeActive = await getIsPromoCodeActive(tokenData?.access_token, queryVal);

        if (isPromoCodeActive) {
          sendAnalyticsForCustomFlows('success', 200);
          fireEvent('redeem_done', {
            source: screenName,
          });
          onUserChange({
            ...user,
            subscribed: true,
          });
        }
      } catch (err) {
        monetizationPopupTrigger(false);
        sendAnalyticsForCustomFlows('fail', 500, err?.message);
        setIsErrorModalOpen(true);
        setErrMessage(err.message);
      }
    } else {
      monetizationPopupTrigger(false);
      setIsErrorModalOpen(true);
      setErrMessage(defaultErrorMessage);
    }
  }, [
    monetizationPopupTrigger,
    searchParams,
    tokenData?.access_token,
    sendAnalyticsForCustomFlows,
    fireEvent,
    screenName,
    onUserChange,
    user,
  ]);

  const discordNitroFlow = useCallback(() => {
    fetchData(`${apiUrl}/discord/link`, {
      method: 'GET',
    })
      .then(res => {
        if (res.status === 'error') {
          sendAnalyticsForCustomFlows('fail', res?.statusCode, res.message);
          setErrMessage(res.message);
          setIsErrorModalOpen(true);
        } else {
          sendAnalyticsForCustomFlows('success', 200);
          window.location.href = res?.response;
        }
      })
      .catch(error => {
        sendAnalyticsForCustomFlows('fail', error?.status_code || 500, error?.message);
        setErrMessage(error?.message);
        setIsErrorModalOpen(true);
      });
  }, [sendAnalyticsForCustomFlows]);

  const customFlowInit = useCallback(
    (newUser?: UserModel) => {
      if (isDiscord && isPartnership && (user || newUser)) {
        discordNitroFlow();
      }

      if ((user || newUser) && user?.account_type !== 'temporary' && customRedeemFlow) {
        void checkPromoIsActive();
      }
    },
    [checkPromoIsActive, customRedeemFlow, discordNitroFlow, isDiscord, isPartnership, user],
  );

  return {
    errMessage,
    setErrMessage,
    isErrorModalOpen,
    setIsErrorModalOpen,
    customFlowInit,
    modalView,
    isDiscord,
    isCustomWelcomePopup,
    redeemApplied: redeemApplied.current,
  };
};

export default useCustomFlows;
